import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMasterAccess } from '../pages/master/masterShared'

// "+ Add a new …" in a dropdown that picks from Master Files. Choosing it
// opens that Master Files page to create one; after saving (or cancelling)
// the page comes back here (?return=). One listener in the app handles every
// such option, so a dropdown only needs <CreateOption kind="vendor" />.

const PREFIX = '__new__:'
export const CREATE = {
  product: { label: '+ Add a new product or service', to: '/master/products/new', can: 'items' },
  customer: { label: '+ Add a new customer', to: '/master/customers/new', can: 'customers' },
  vendor: { label: '+ Add a new vendor', to: '/master/vendors/new', can: 'vendors' },
  location: { label: '+ Add a new location', to: '/master/locations/new', can: 'locations' },
  account: { label: '+ Add a new account', to: '/master/accounts/new', can: 'accounts' },
}

/** The option itself; nothing for people who can't create that kind.
 *  params start the new one off, e.g. { stock_kind: 'material' }. */
export function CreateOption({ kind, params }) {
  const can = useMasterAccess()
  const c = CREATE[kind]
  if (!c || !can[c.can]) return null
  const q = params ? new URLSearchParams(params).toString() : ''
  return <option value={`${PREFIX}${kind}${q ? `?${q}` : ''}`}>{c.label}</option>
}

/** Where a create page goes after saving or cancelling: back to ?return=
 *  (a path in this app), else its own list. */
export function returnTo(search, fallback) {
  const r = new URLSearchParams(search).get('return') || ''
  return r.startsWith('/') && !r.startsWith('//') ? r : fallback
}

/** In the app shell: open the create page when a "+ Add a new …" option is
 *  chosen, before the dropdown's own onChange sees it. */
export function useCreateOptions() {
  const nav = useNavigate()
  const { pathname, search } = useLocation()
  useEffect(() => {
    const before = new WeakMap()
    const isSelect = (el) => el && el.tagName === 'SELECT'
    const remember = (e) => { if (isSelect(e.target)) before.set(e.target, e.target.value) }
    const onChange = (e) => {
      const el = e.target
      if (!isSelect(el) || !String(el.value).startsWith(PREFIX)) return
      e.stopPropagation() // the dropdown keeps what it had
      const [kind, q] = el.value.slice(PREFIX.length).split('?')
      const c = CREATE[kind]
      el.value = before.get(el) ?? ''
      if (c) nav(`${c.to}?${q ? `${q}&` : ''}return=${encodeURIComponent(pathname + search)}`)
    }
    document.addEventListener('focusin', remember, true)
    document.addEventListener('pointerdown', remember, true)
    document.addEventListener('change', onChange, true)
    return () => {
      document.removeEventListener('focusin', remember, true)
      document.removeEventListener('pointerdown', remember, true)
      document.removeEventListener('change', onChange, true)
    }
  }, [nav, pathname, search])
}
